// src/stats/TopClients.jsx
import React, { useState, useEffect } from "react";
import {
    Card, Row, Col, Button, Table, Form,
    Pagination, Tooltip, OverlayTrigger
} from "react-bootstrap";
import { Link, useNavigate } from "react-router-dom";
import { formatCurrency, formatDate, formatNumber } from "../utils/formatters";
import { API_BASE_URL } from "../constants/api";
import { COLORS } from "../constants/colors";
import Footer from "../layouts/Footer";
import Header from "../layouts/Header";

/* ============================================================
   TOAST NOTIFICATION — Design charte
   ============================================================ */
const ToastNotification = ({ show, onClose, type, title, message, autoHideDelay = 5000 }) => {
    if (!show) return null;
    const config = {
        success: { bgColor: COLORS.success || '#10b981', icon: 'ri-checkbox-circle-fill' },
        error:   { bgColor: COLORS.danger  || '#ef4444', icon: 'ri-error-warning-fill' },
        info:    { bgColor: COLORS.info    || '#3b82f6', icon: 'ri-information-fill' },
        warning: { bgColor: COLORS.warning || '#f59e0b', icon: 'ri-alert-fill' }
    }[type] || { bgColor: COLORS.info || '#3b82f6', icon: 'ri-information-fill' };

    return (
        <div className="toast-notification-fixed">
            <div className="toast-modern">
                <div className="toast-header-custom" style={{ background: config.bgColor }}>
                    <div className="d-flex align-items-center gap-2">
                        <i className={`${config.icon} text-white`} style={{ fontSize: '1.5rem' }}></i>
                        <strong className="text-white fs-6">{title}</strong>
                    </div>
                    <button onClick={onClose} className="toast-close-btn">
                        <i className="ri-close-line fs-5"></i>
                    </button>
                </div>
                <div className="toast-body-custom"><p className="mb-0">{message}</p></div>
                <div className="toast-progress" style={{ background: config.bgColor, animation: `shrink ${autoHideDelay}ms linear forwards` }}></div>
            </div>
        </div>
    );
};

/* ============================================================
   COMPOSANT PRINCIPAL
   ============================================================ */
export default function TopClients() {
    const navigate = useNavigate();
    const [data, setData] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [periode, setPeriode] = useState('mois');
    const [itemsPerPage, setItemsPerPage] = useState(10);
    const [currentPage, setCurrentPage] = useState(1);
    const [sortConfig, setSortConfig] = useState({ key: 'total_achats', direction: 'desc' });
    const [toast, setToast] = useState({ show: false, type: '', title: '', message: '' });

    const showToast = (type, title, message) => setToast({ show: true, type, title, message });
    const hideToast = () => setToast({ show: false, type: '', title: '', message: '' });

    useEffect(() => {
        fetchData();
    }, [periode]);

    /* ============================================================
       FETCH
       ============================================================ */
    const fetchData = async () => {
        try {
            setLoading(true);
            const token = localStorage.getItem("token");
            const response = await fetch(
                `${API_BASE_URL}/stats.php?action=top_clients&periode=${periode}`,
                { headers: { 'Authorization': `Bearer ${token}` } }
            );
            if (!response.ok) throw new Error("Erreur chargement");
            const result = await response.json();
            setData(result.data || []);
            setCurrentPage(1);
        } catch (err) {
            setError(err.message);
            showToast('error', 'Erreur', err.message);
        } finally {
            setLoading(false);
        }
    };

    /* ============================================================
       HELPERS
       ============================================================ */
    const getPeriodeLabel = () => {
        switch(periode) {
            case 'mois': return "ce mois";
            case 'trimestre': return "ce trimestre";
            case 'annee': return "cette année";
            default: return "cette période";
        }
    };

    const getPeriodeIcon = () => {
        switch(periode) {
            case 'mois': return "ri-calendar-line";
            case 'trimestre': return "ri-calendar-2-line";
            case 'annee': return "ri-calendar-check-line";
            default: return "ri-calendar-line";
        }
    };

    const sortedData = React.useMemo(() => {
        const sortableData = [...data];
        if (sortConfig.key) {
            sortableData.sort((a, b) => {
                let aVal = a[sortConfig.key];
                let bVal = b[sortConfig.key];
                if (sortConfig.key === 'total_achats' || sortConfig.key === 'panier_moyen' || sortConfig.key === 'nb_factures') {
                    aVal = parseFloat(aVal) || 0;
                    bVal = parseFloat(bVal) || 0;
                } else {
                    aVal = aVal?.toString().toLowerCase() || '';
                    bVal = bVal?.toString().toLowerCase() || '';
                }
                if (aVal < bVal) return sortConfig.direction === 'asc' ? -1 : 1;
                if (aVal > bVal) return sortConfig.direction === 'asc' ? 1 : -1;
                return 0;
            });
        }
        return sortableData;
    }, [data, sortConfig]);

    const totalPages = Math.ceil(sortedData.length / itemsPerPage);
    const startIndex = (currentPage - 1) * itemsPerPage;
    const currentItems = sortedData.slice(startIndex, startIndex + itemsPerPage);

    const handleSort = (key) => {
        setSortConfig(prev => ({
            key,
            direction: prev.key === key && prev.direction === 'asc' ? 'desc' : 'asc'
        }));
    };

    const totalAchats = sortedData.reduce((sum, item) => sum + (parseFloat(item.total_achats) || 0), 0);
    const totalFactures = sortedData.reduce((sum, item) => sum + (parseInt(item.nb_factures) || 0), 0);
    const moyenneAchats = sortedData.length > 0 ? totalAchats / sortedData.length : 0;

    /* ============================================================
       LOADING — identique à l'original
       ============================================================ */
    if (loading) {
        return (
            <>
                <Header />
                <div className="main main-app p-3 p-lg-4" style={{ background: '#f8fafc', minHeight: '100vh' }}>
                    <div className="d-flex justify-content-center align-items-center" style={{ minHeight: 'calc(100vh - 100px)' }}>
                        <div className="text-center" style={{ maxWidth: '400px' }}>
                            <div className="loading-ring-wrapper mb-4">
                                <div className="loading-ring"></div>
                                <div className="loading-ring-inner"></div>
                                <i className="ri-loader-4-fill loading-icon"></i>
                            </div>
                            <h5 className="fw-bold" style={{ color: COLORS.primaryDark, letterSpacing: '0.3px' }}>
                                Analyse des meilleurs clients...
                            </h5>
                            <p className="text-muted small mb-0">Veuillez patienter quelques instants...</p>
                            <div className="mx-auto mt-4" style={{ width: '80%' }}>
                                <div className="progress" style={{ height: '3px', background: '#e9ecef', borderRadius: '10px', overflow: 'hidden' }}>
                                    <div
                                        className="progress-bar progress-bar-striped progress-bar-animated"
                                        style={{
                                            width: '100%',
                                            background: `linear-gradient(90deg, ${COLORS.primary}, ${COLORS.secondary}, ${COLORS.primary})`,
                                            backgroundSize: '200% 100%',
                                            animation: 'shimmer 1.5s linear infinite'
                                        }}
                                    />
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
                <Footer />
                <style>{`
                    .loading-ring-wrapper { position: relative; width: 80px; height: 80px; margin: 0 auto; }
                    .loading-ring { position: absolute; top: 0; left: 0; width: 100%; height: 100%; border-radius: 50%; border: 4px solid rgba(33,172,205,0.15); border-top-color: ${COLORS.primary}; animation: spin 1s linear infinite; }
                    .loading-ring-inner { position: absolute; top: 8px; left: 8px; right: 8px; bottom: 8px; border-radius: 50%; border: 3px solid rgba(33,172,205,0.08); border-bottom-color: ${COLORS.secondary}; animation: spin 0.8s linear infinite reverse; }
                    .loading-icon { position: absolute; top: 50%; left: 50%; transform: translate(-50%, -50%); font-size: 28px; color: ${COLORS.primary}; animation: pulse-icon 1.2s ease-in-out infinite; }
                    @keyframes spin { to { transform: rotate(360deg); } }
                    @keyframes pulse-icon { 0%,100% { transform: translate(-50%,-50%) scale(1); opacity:1; } 50% { transform: translate(-50%,-50%) scale(1.15); opacity:0.6; } }
                    @keyframes shimmer { 0% { background-position: 200% 0; } 100% { background-position: -200% 0; } }
                `}</style>
            </>
        );
    }

    /* ============================================================
       RENDU PRINCIPAL
       ============================================================ */
    return (
        <>
            <Header />
            <div className="main main-app p-3 p-lg-4" style={{ background: '#f8fafc', minHeight: '100vh' }}>

                <ToastNotification
                    show={toast.show}
                    onClose={hideToast}
                    type={toast.type}
                    title={toast.title}
                    message={toast.message}
                />

                <div className="container-fluid">

                    {/* ===== EN-TÊTE MODERNE ===== */}
                    <div className="header-modern mb-4">
                        <div className="header-left">
                            <div className="header-icon-wrapper">
                                <i className="ri-group-line"></i>
                            </div>
                            <div>
                                <h5 className="header-title">Top clients</h5>
                                <div className="d-flex flex-wrap gap-2 mt-1">
                                    <span className="badge-modern">
                                        <i className={getPeriodeIcon()}></i> {getPeriodeLabel()}
                                    </span>
                                    <span className="badge-modern" style={{ background: `${COLORS.success}15`, color: COLORS.success }}>
                                        <i className="ri-user-star-line"></i> {sortedData.length} clients
                                    </span>
                                </div>
                            </div>
                        </div>
                        <div className="d-flex gap-2 flex-wrap">
                            <Button className="btn-modern-outline" onClick={() => navigate('/stats/dashboard')}>
                                <i className="ri-arrow-left-line"></i> Retour
                            </Button>
                        </div>
                    </div>

                    {/* ===== KPI CARDS ===== */}
                    <Row className="g-3 mb-4">
                        <Col sm="6" xl="4">
                            <div className="kpi-charte kpi-primary">
                                <div className="kpi-icon-charte"><i className="ri-money-euro-circle-line"></i></div>
                                <div className="kpi-content-charte">
                                    <span className="kpi-label-charte">Total achats</span>
                                    <h3 className="kpi-value-charte">{formatCurrency(totalAchats)}</h3>
                                    <span className="kpi-unit-charte">cumul période</span>
                                </div>
                            </div>
                        </Col>
                        <Col sm="6" xl="4">
                            <div className="kpi-charte kpi-info">
                                <div className="kpi-icon-charte"><i className="ri-file-list-line"></i></div>
                                <div className="kpi-content-charte">
                                    <span className="kpi-label-charte">Total factures</span>
                                    <h3 className="kpi-value-charte">{formatNumber(totalFactures)}</h3>
                                    <span className="kpi-unit-charte">documents</span>
                                </div>
                            </div>
                        </Col>
                        <Col sm="6" xl="4">
                            <div className="kpi-charte kpi-success">
                                <div className="kpi-icon-charte"><i className="ri-calculator-line"></i></div>
                                <div className="kpi-content-charte">
                                    <span className="kpi-label-charte">Achat moyen</span>
                                    <h3 className="kpi-value-charte" style={{ fontSize: '1rem' }}>{formatCurrency(moyenneAchats)}</h3>
                                    <span className="kpi-unit-charte">par client</span>
                                </div>
                            </div>
                        </Col>
                    </Row>

                    {/* ===== FILTRES ===== */}
                    <Card className="filter-card-charte mb-4">
                        <Card.Body className="py-3 px-4">
                            <Row className="align-items-end g-3">
                                <Col md="4">
                                    <Form.Group>
                                        <Form.Label className="form-label-modern text-white">
                                            <i className={`${getPeriodeIcon()} me-2`}></i>
                                            Période
                                        </Form.Label>
                                        <Form.Select
                                            value={periode}
                                            onChange={(e) => setPeriode(e.target.value)}
                                            className="filter-select-charte"
                                        >
                                            <option value="mois">Ce mois</option>
                                            <option value="trimestre">Ce trimestre</option>
                                            <option value="annee">Cette année</option>
                                        </Form.Select>
                                    </Form.Group>
                                </Col>
                                <Col md="4">
                                    <Form.Group>
                                        <Form.Label className="form-label-modern text-white">
                                            <i className="ri-list-settings-line me-2"></i>
                                            Affichage
                                        </Form.Label>
                                        <Form.Select
                                            value={itemsPerPage}
                                            onChange={(e) => {
                                                setItemsPerPage(parseInt(e.target.value));
                                                setCurrentPage(1);
                                            }}
                                            className="filter-select-charte"
                                        >
                                            <option value="5">5 par page</option>
                                            <option value="10">10 par page</option>
                                            <option value="20">20 par page</option>
                                            <option value="50">50 par page</option>
                                        </Form.Select>
                                    </Form.Group>
                                </Col>
                                <Col md="4">
                                    <Button className="btn-modern-primary w-100 justify-content-center" onClick={fetchData}>
                                        <i className="ri-refresh-line me-1"></i> Actualiser
                                    </Button>
                                </Col>
                            </Row>
                        </Card.Body>
                    </Card>

                    {/* ===== TABLEAU ===== */}
                    <Card className="border-0 shadow-sm rounded-3 overflow-hidden">
                        <Card.Header className="card-header-gradient">
                            <div className="d-flex justify-content-between align-items-center flex-wrap gap-2">
                                <div className="d-flex align-items-center gap-2">
                                    <i className="ri-group-line text-white fs-5"></i>
                                    <span className="fw-semibold text-white">Classement des clients</span>
                                    <span className="badge-header-light">
                                        {startIndex + 1}−{Math.min(startIndex + itemsPerPage, sortedData.length)} sur {sortedData.length}
                                    </span>
                                </div>
                            </div>
                        </Card.Header>
                        <Card.Body className="p-0">
                            <div className="table-responsive">
                                <Table hover className="align-middle mb-0 table-charte">
                                    <thead>
                                        <tr>
                                            <th className="ps-4" style={{ width: '70px' }}>#</th>
                                            <th className="sortable-charte" onClick={() => handleSort('raison_sociale')}>
                                                Client
                                                {sortConfig.key === 'raison_sociale' && (
                                                    <i className={`ri-arrow-${sortConfig.direction === 'asc' ? 'up' : 'down'}-s-line ms-1`}></i>
                                                )}
                                            </th>
                                            <th className="text-end sortable-charte" onClick={() => handleSort('nb_factures')}>
                                                Factures
                                                {sortConfig.key === 'nb_factures' && (
                                                    <i className={`ri-arrow-${sortConfig.direction === 'asc' ? 'up' : 'down'}-s-line ms-1`}></i>
                                                )}
                                            </th>
                                            <th className="text-end sortable-charte" onClick={() => handleSort('total_achats')}>
                                                Total achats
                                                {sortConfig.key === 'total_achats' && (
                                                    <i className={`ri-arrow-${sortConfig.direction === 'asc' ? 'up' : 'down'}-s-line ms-1`}></i>
                                                )}
                                            </th>
                                            <th className="text-end sortable-charte" onClick={() => handleSort('panier_moyen')}>
                                                Panier moyen
                                                {sortConfig.key === 'panier_moyen' && (
                                                    <i className={`ri-arrow-${sortConfig.direction === 'asc' ? 'up' : 'down'}-s-line ms-1`}></i>
                                                )}
                                            </th>
                                            <th className="sortable-charte" onClick={() => handleSort('dernier_achat')}>
                                                Dernier achat
                                                {sortConfig.key === 'dernier_achat' && (
                                                    <i className={`ri-arrow-${sortConfig.direction === 'asc' ? 'up' : 'down'}-s-line ms-1`}></i>
                                                )}
                                            </th>
                                            <th className="text-center pe-4">Actions</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {currentItems.map((client, index) => {
                                            const globalIndex = startIndex + index;
                                            const medalGradient = globalIndex === 0
                                                ? 'linear-gradient(135deg, #f59e0b, #d97706)'
                                                : globalIndex === 1
                                                    ? 'linear-gradient(135deg, #94a3b8, #64748b)'
                                                    : globalIndex === 2
                                                        ? 'linear-gradient(135deg, #cd7f32, #a0522d)'
                                                        : 'linear-gradient(135deg, #64748b, #475569)';
                                            const part = totalAchats > 0
                                                ? ((parseFloat(client.total_achats) || 0) / totalAchats * 100).toFixed(1)
                                                : 0;
                                            return (
                                                <tr key={client.id_client}>
                                                    <td className="ps-4">
                                                        <span className="ranking-badge-charte" style={{ background: medalGradient }}>
                                                            {globalIndex + 1}
                                                        </span>
                                                    </td>
                                                    <td>
                                                        <div className="d-flex align-items-center gap-2">
                                                            <div className="avatar-icon-charte">
                                                                <i className="ri-user-3-line"></i>
                                                            </div>
                                                            <div>
                                                                <Link to={`/ventes/clientsDetails/${client.id_client}`} className="table-link-charte">
                                                                    {client.raison_sociale}
                                                                </Link>
                                                                <div className="d-flex gap-2 flex-wrap">
                                                                    <small className="text-muted">{client.code_client}</small>
                                                                    {client.telephone && (
                                                                        <small className="text-muted">
                                                                            <i className="ri-phone-line me-1"></i>
                                                                            {client.telephone}
                                                                        </small>
                                                                    )}
                                                                </div>
                                                            </div>
                                                        </div>
                                                    </td>
                                                    <td className="text-end">
                                                        <span className="quantity-badge-charte">{client.nb_factures}</span>
                                                    </td>
                                                    <td className="text-end">
                                                        <div className="fw-bold" style={{ color: COLORS.primary }}>
                                                            {formatCurrency(client.total_achats)}
                                                        </div>
                                                        <div className="progress-bar-charte mt-1 ms-auto" style={{ width: '80px' }}>
                                                            <div className="progress-fill-charte"
                                                                 style={{
                                                                     width: `${parseFloat(part)}%`,
                                                                     background: COLORS.primary
                                                                 }}>
                                                            </div>
                                                        </div>
                                                    </td>
                                                    <td className="text-end fw-semibold" style={{ color: COLORS.success }}>
                                                        {formatCurrency(client.panier_moyen)}
                                                    </td>
                                                    <td>
                                                        <span className="text-muted">
                                                            <i className="ri-calendar-line me-1"></i>
                                                            {formatDate(client.dernier_achat)}
                                                        </span>
                                                    </td>
                                                    <td className="text-center pe-4">
                                                        <OverlayTrigger placement="top" overlay={<Tooltip>Voir le client</Tooltip>}>
                                                            <button
                                                                className="action-btn-charte"
                                                                onClick={() => navigate(`/ventes/clientsDetails/${client.id_client}`)}
                                                                style={{ '--btn-color': COLORS.primary }}
                                                            >
                                                                <i className="ri-eye-line"></i>
                                                            </button>
                                                        </OverlayTrigger>
                                                    </td>
                                                </tr>
                                            );
                                        })}
                                        {currentItems.length === 0 && (
                                            <tr>
                                                <td colSpan="7">
                                                    <div className="empty-state-charte">
                                                        <div className="empty-state-icon-charte">
                                                            <i className="ri-inbox-line"></i>
                                                        </div>
                                                        <h6 className="fw-semibold mb-1">Aucune donnée disponible</h6>
                                                        <p className="text-muted small mb-0">Aucun client actif sur cette période</p>
                                                    </div>
                                                </td>
                                            </tr>
                                        )}
                                    </tbody>
                                </Table>
                            </div>
                        </Card.Body>
                    </Card>

                    {/* ===== PAGINATION ===== */}
                    {totalPages > 1 && !loading && (
                        <div className="pagination-wrapper-charte mt-3">
                            <div className="pagination-info-charte">
                                Affichage de <strong>{startIndex + 1}</strong> à <strong>{Math.min(startIndex + itemsPerPage, sortedData.length)}</strong> sur <strong>{sortedData.length}</strong> clients
                            </div>
                            <Pagination className="pagination-charte mb-0">
                                <Pagination.Prev onClick={() => setCurrentPage(p => Math.max(1, p - 1))} disabled={currentPage === 1} />
                                {[...Array(Math.min(5, totalPages))].map((_, i) => {
                                    let pageNum;
                                    if (totalPages <= 5) pageNum = i + 1;
                                    else if (currentPage <= 3) pageNum = i + 1;
                                    else if (currentPage >= totalPages - 2) pageNum = totalPages - 4 + i;
                                    else pageNum = currentPage - 2 + i;
                                    return (
                                        <Pagination.Item key={i} active={pageNum === currentPage} onClick={() => setCurrentPage(pageNum)}>
                                            {pageNum}
                                        </Pagination.Item>
                                    );
                                })}
                                <Pagination.Next onClick={() => setCurrentPage(p => Math.min(totalPages, p + 1))} disabled={currentPage === totalPages} />
                            </Pagination>
                        </div>
                    )}

                    {/* ===== TOTAUX ===== */}
                    {sortedData.length > 0 && (
                        <Card className="border-0 shadow-sm rounded-3 mt-3">
                            <Card.Body className="p-4">
                                <div className="d-flex justify-content-between align-items-center flex-wrap gap-3">
                                    <div className="d-flex align-items-center gap-2">
                                        <div className="section-icon-charte" style={{ background: `${COLORS.primary}15`, color: COLORS.primary }}>
                                            <i className="ri-information-line"></i>
                                        </div>
                                        <span className="fw-semibold">
                                            Total {getPeriodeLabel()} : {sortedData.length} clients actifs
                                        </span>
                                    </div>
                                    <div className="d-flex gap-4 flex-wrap">
                                        <div className="text-end">
                                            <small className="text-muted d-block">Total achats</small>
                                            <span className="fw-bold" style={{ color: COLORS.primary }}>{formatCurrency(totalAchats)}</span>
                                        </div>
                                        <div className="text-end">
                                            <small className="text-muted d-block">Factures</small>
                                            <span className="fw-bold" style={{ color: COLORS.info }}>{formatNumber(totalFactures)}</span>
                                        </div>
                                        <div className="text-end">
                                            <small className="text-muted d-block">Achat moyen</small>
                                            <span className="fw-bold" style={{ color: COLORS.success }}>{formatCurrency(moyenneAchats)}</span>
                                        </div>
                                    </div>
                                </div>
                            </Card.Body>
                        </Card>
                    )}
                </div>
                <Footer />
            </div>

            {/* ===== STYLES CHARTE ===== */}
            <style>{`
                :root {
                    --primary: ${COLORS.primary || '#295AA7'};
                    --primary-dark: ${COLORS.primaryDark || '#1e3f7a'};
                    --secondary: ${COLORS.secondary || '#21ACCD'};
                    --success: ${COLORS.success || '#10b981'};
                    --danger: ${COLORS.danger || '#ef4444'};
                    --warning: ${COLORS.warning || '#f59e0b'};
                    --info: ${COLORS.info || '#3b82f6'};
                }

                /* ===== EN-TÊTE ===== */
                .header-modern {
                    background: white; padding: 1.2rem 1.8rem;
                    box-shadow: 0 2px 8px rgba(0,0,0,0.04);
                    border-left: 4px solid var(--secondary);
                    border-radius: 12px;
                    display: flex; align-items: center; justify-content: space-between;
                    flex-wrap: wrap; gap: 1rem;
                }
                .header-left { display: flex; align-items: center; gap: 1rem; }
                .header-icon-wrapper {
                    width: 52px; height: 52px;
                    background: linear-gradient(135deg, var(--primary), var(--secondary));
                    display: flex; align-items: center; justify-content: center;
                    border-radius: 14px; color: white; font-size: 1.5rem;
                    box-shadow: 0 4px 12px rgba(41, 90, 167, 0.25);
                }
                .header-title { color: #1e293b; font-size: 1.3rem; font-weight: 700; margin: 0; }
                .badge-modern {
                    background: #f1f4f9; color: #475569;
                    padding: 0.3rem 0.8rem; border-radius: 50px;
                    font-size: 0.75rem; font-weight: 600;
                    display: inline-flex; align-items: center; gap: 6px;
                }
                .badge-modern i { color: var(--secondary); }

                /* ===== FILTRES ===== */
                .filter-card-charte {
                    background: linear-gradient(135deg, var(--primary-dark), var(--primary));
                    border-radius: 12px; border: none;
                    box-shadow: 0 8px 24px rgba(41,90,167,0.15);
                }
                .form-label-modern {
                    font-weight: 600; font-size: 0.8rem;
                    margin-bottom: 0.4rem;
                    text-transform: uppercase; letter-spacing: 0.3px;
                }
                .filter-select-charte {
                    background: white !important;
                    border: none !important;
                    border-radius: 50px !important;
                    padding: 0.6rem 1rem !important;
                    font-size: 0.9rem;
                    cursor: pointer;
                    box-shadow: 0 4px 12px rgba(0,0,0,0.08);
                }
                .filter-select-charte:focus { box-shadow: 0 0 0 3px rgba(255,255,255,0.2) !important; }

                /* ===== KPI ===== */
                .kpi-charte {
                    background: white; border-radius: 12px;
                    padding: 1.2rem;
                    display: flex; align-items: center; gap: 1rem;
                    box-shadow: 0 2px 8px rgba(0,0,0,0.04);
                    border-left: 4px solid;
                    transition: all 0.25s ease;
                    height: 100%;
                }
                .kpi-charte:hover {
                    transform: translateY(-3px);
                    box-shadow: 0 12px 28px rgba(0,0,0,0.08);
                }
                .kpi-primary { border-left-color: var(--primary); }
                .kpi-success { border-left-color: var(--success); }
                .kpi-danger  { border-left-color: var(--danger); }
                .kpi-info    { border-left-color: var(--info); }
                .kpi-warning { border-left-color: var(--warning); }
                .kpi-icon-charte {
                    width: 48px; height: 48px; border-radius: 12px;
                    display: flex; align-items: center; justify-content: center;
                    font-size: 1.4rem; flex-shrink: 0;
                }
                .kpi-primary .kpi-icon-charte { background: ${COLORS.primary}15; color: var(--primary); }
                .kpi-success .kpi-icon-charte { background: ${COLORS.success}15; color: var(--success); }
                .kpi-danger  .kpi-icon-charte { background: ${COLORS.danger}15; color: var(--danger); }
                .kpi-info    .kpi-icon-charte { background: ${COLORS.info}15; color: var(--info); }
                .kpi-warning .kpi-icon-charte { background: ${COLORS.warning}15; color: var(--warning); }
                .kpi-content-charte { flex: 1; min-width: 0; }
                .kpi-label-charte {
                    font-size: 0.7rem; color: #94a3b8;
                    text-transform: uppercase; font-weight: 600;
                    letter-spacing: 0.5px;
                }
                .kpi-value-charte {
                    font-size: 1.35rem; font-weight: 700;
                    margin: 0.3rem 0; color: #1e293b;
                }
                .kpi-unit-charte { font-size: 0.7rem; color: #94a3b8; }

                /* ===== HEADER GRADIENT ===== */
                .card-header-gradient {
                    background: linear-gradient(135deg, var(--primary) 0%, var(--primary-dark) 100%);
                    border-top-left-radius: 12px !important;
                    border-top-right-radius: 12px !important;
                    padding: 1rem 1.5rem;
                    border: none;
                }
                .badge-header-light {
                    background: rgba(255,255,255,0.2);
                    color: white;
                    border: 1px solid rgba(255,255,255,0.3);
                    border-radius: 50px;
                    padding: 0.25rem 0.75rem;
                    font-size: 0.75rem;
                    font-weight: 600;
                }

                /* ===== TABLE ===== */
                .table-charte thead th {
                    background: #f8fafc; color: #475569;
                    font-weight: 600; font-size: 0.7rem;
                    text-transform: uppercase; letter-spacing: 0.5px;
                    border-bottom: 2px solid #e2e8f0;
                    padding: 0.9rem 0.75rem;
                }
                .table-charte tbody td {
                    padding: 0.9rem 0.75rem;
                    vertical-align: middle;
                    border-bottom: 1px solid #f1f5f9;
                    font-size: 0.85rem;
                }
                .table-charte tbody tr { transition: background 0.15s ease; }
                .table-charte tbody tr:hover { background: #f8fafc; }
                .sortable-charte {
                    cursor: pointer;
                    user-select: none;
                }
                .sortable-charte:hover {
                    color: var(--primary);
                    background: #f1f5f9 !important;
                }
                .table-link-charte {
                    color: var(--primary);
                    text-decoration: none;
                    font-weight: 600;
                    font-size: 0.85rem;
                }
                .table-link-charte:hover { text-decoration: underline; }
                .quantity-badge-charte {
                    display: inline-flex; align-items: center; justify-content: center;
                    min-width: 45px; padding: 0.25rem 0.6rem;
                    background: #f1f5f9; border-radius: 12px;
                    font-weight: 600; font-size: 0.8rem; color: #475569;
                }

                /* ===== RANKING BADGE ===== */
                .ranking-badge-charte {
                    width: 36px; height: 36px;
                    border-radius: 50%;
                    display: inline-flex;
                    align-items: center;
                    justify-content: center;
                    font-weight: 700;
                    font-size: 0.9rem;
                    color: white;
                    box-shadow: 0 2px 8px rgba(0,0,0,0.15);
                }

                /* ===== AVATAR ===== */
                .avatar-icon-charte {
                    width: 40px; height: 40px;
                    background: ${COLORS.primary}15;
                    border-radius: 50%;
                    display: flex; align-items: center; justify-content: center;
                    color: var(--primary);
                    flex-shrink: 0;
                    font-size: 1.2rem;
                }

                /* ===== PROGRESS ===== */
                .progress-bar-charte {
                    background: #e2e8f0;
                    border-radius: 4px;
                    height: 4px;
                    overflow: hidden;
                    display: block;
                }
                .progress-fill-charte {
                    height: 100%;
                    border-radius: 4px;
                    transition: width 0.3s ease;
                }

                /* ===== ACTION BTN ===== */
                .action-btn-charte {
                    width: 36px; height: 36px; border-radius: 10px;
                    display: inline-flex; align-items: center; justify-content: center;
                    background: white; border: 1.5px solid #e2e8f0;
                    color: #64748b; font-size: 1rem;
                    transition: all 0.2s ease;
                    cursor: pointer;
                }
                .action-btn-charte:hover {
                    border-color: var(--btn-color); color: var(--btn-color);
                    transform: translateY(-2px);
                    box-shadow: 0 6px 16px rgba(41, 90, 167, 0.15);
                }

                /* ===== EMPTY STATE ===== */
                .empty-state-charte { text-align: center; padding: 3rem 2rem; }
                .empty-state-icon-charte {
                    width: 72px; height: 72px;
                    background: linear-gradient(135deg, #f1f5f9, #e2e8f0);
                    border-radius: 50%;
                    display: flex; align-items: center; justify-content: center;
                    margin: 0 auto 1rem; font-size: 1.8rem; color: #94a3b8;
                }

                /* ===== SECTION ===== */
                .section-icon-charte {
                    width: 34px; height: 34px; border-radius: 10px;
                    display: inline-flex; align-items: center; justify-content: center;
                    font-size: 1rem;
                }

                /* ===== PAGINATION ===== */
                .pagination-wrapper-charte {
                    display: flex; justify-content: space-between; align-items: center;
                    padding: 1rem 1.5rem;
                    background: white;
                    border-radius: 12px;
                    box-shadow: 0 2px 8px rgba(0,0,0,0.04);
                    flex-wrap: wrap; gap: 1rem;
                }
                .pagination-info-charte { font-size: 0.8rem; color: #64748b; }
                .pagination-charte { gap: 6px; }
                .pagination-charte .page-item .page-link {
                    border-radius: 10px;
                    border: 1.5px solid #e2e8f0;
                    color: #1e293b;
                    padding: 0.5rem 0.9rem;
                    font-weight: 500; font-size: 0.85rem;
                    transition: all 0.2s;
                }
                .pagination-charte .page-item.active .page-link {
                    background: linear-gradient(135deg, var(--primary), var(--primary-dark));
                    border-color: var(--primary);
                    color: white;
                    box-shadow: 0 4px 12px rgba(41,90,167,0.3);
                }
                .pagination-charte .page-item .page-link:hover {
                    background: #f1f5f9;
                    border-color: var(--secondary);
                    color: var(--primary);
                    transform: translateY(-1px);
                }

                /* ===== BOUTONS ===== */
                .btn-modern-outline {
                    border: 1.5px solid #d1d5db; background: white;
                    padding: 0.5rem 1.4rem; border-radius: 50px;
                    font-size: 0.85rem; font-weight: 500; color: #4b5563;
                    transition: all 0.2s ease;
                    display: inline-flex; align-items: center; gap: 6px;
                }
                .btn-modern-outline:hover {
                    border-color: var(--secondary); color: var(--secondary);
                    background: #f0fbff; transform: translateY(-1px);
                    box-shadow: 0 4px 8px rgba(33,172,205,0.15);
                }
                .btn-modern-primary {
                    background: linear-gradient(135deg, var(--primary), var(--primary-dark));
                    border: none; color: white; padding: 0.5rem 1.6rem;
                    border-radius: 50px; font-weight: 600; font-size: 0.85rem;
                    transition: all 0.2s ease;
                    box-shadow: 0 4px 12px rgba(41,90,167,0.3);
                    display: inline-flex; align-items: center; gap: 6px;
                }
                .btn-modern-primary:hover {
                    transform: translateY(-2px);
                    box-shadow: 0 6px 18px rgba(41,90,167,0.4); color: white;
                }
                .btn-modern-outline:disabled,
                .btn-modern-primary:disabled { opacity: 0.6; transform: none; }

                /* ===== TOAST ===== */
                .toast-notification-fixed {
                    position: fixed; bottom: 24px; right: 24px;
                    z-index: 9999; animation: slideInRight 0.3s ease-out;
                }
                .toast-modern {
                    background: white; border-radius: 16px;
                    box-shadow: 0 20px 40px rgba(0,0,0,0.15);
                    min-width: 320px; max-width: 400px; overflow: hidden;
                }
                .toast-header-custom {
                    padding: 12px 20px;
                    display: flex; align-items: center; justify-content: space-between;
                }
                .toast-close-btn {
                    background: transparent; border: none;
                    color: white; cursor: pointer; opacity: 0.8;
                }
                .toast-close-btn:hover { opacity: 1; }
                .toast-body-custom { padding: 16px 20px; }
                .toast-body-custom p { color: #1e293b; font-size: 0.9rem; }
                .toast-progress { height: 3px; width: 100%; animation: shrink linear forwards; }

                /* ===== ANIMATIONS ===== */
                @keyframes slideInRight {
                    from { opacity: 0; transform: translateX(100%); }
                    to { opacity: 1; transform: translateX(0); }
                }
                @keyframes shrink {
                    from { width: 100%; }
                    to { width: 0%; }
                }

                /* ===== RESPONSIVE ===== */
                @media (max-width: 768px) {
                    .header-modern { padding: 1rem; }
                    .header-icon-wrapper { width: 44px; height: 44px; font-size: 1.2rem; }
                    .header-title { font-size: 1.1rem; }
                    .pagination-wrapper-charte { flex-direction: column; align-items: stretch; }
                    .pagination-info-charte { text-align: center; }
                    .toast-notification-fixed { bottom: 12px; right: 12px; left: 12px; }
                    .toast-modern { min-width: auto; max-width: none; }
                }
                .rounded-3 { border-radius: 12px !important; }
            `}</style>
        </>
    );
}
